import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import Header from './Header.jsx';
import { domain } from './Domain.jsx';
import useFetch from './useFetch.jsx';
import { useLoginContext } from './LoginContext.jsx';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    Play, ArrowLeft, Clock, Calendar, Eye, Share2, 
    BookOpen, Users, Star, TrendingUp, Loader2, Heart,
    MessageCircle, Download, Bookmark, Volume2, VolumeX,
    Maximize2, Settings, ThumbsUp, ThumbsDown, Flag,
    ChevronLeft, ChevronRight, Zap, Target, Award, Pause, ArrowRight,
    ChevronDown, ChevronUp
} from 'lucide-react';

const HowItWorksDetails = () => {
    let { name: tutorialId } = useParams();
    const navigate = useNavigate();
    const { userInfo, isLoggedIn } = useLoginContext();
    const videoRef = useRef(null);
    const [currentVideo, setCurrentVideo] = useState(null);
    const { data: tutorials, loading: tutorialsLoading } = useFetch(`${domain}/how-it-works`);
    const { data: stats, loading: statsLoading } = useFetch(`${domain}/statistics`);
    const { data: currentTutorial, loading: currentTutorialLoading } = useFetch(`${domain}/how-it-works/${tutorialId}`);
    const [durations, setDurations] = useState({});
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [showControls, setShowControls] = useState(true);
    const [isLiked, setIsLiked] = useState(false);
    const [isBookmarked, setIsBookmarked] = useState(false);
    const [showComments, setShowComments] = useState(false);
    const [comment, setComment] = useState('');
    const [comments, setComments] = useState([]);
    const [commentsLoading, setCommentsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');
    const [replyingTo, setReplyingTo] = useState(null);
    const [replyContent, setReplyContent] = useState('');
    const [hiddenReplies, setHiddenReplies] = useState(new Set());

    useEffect(() => {
        if (!currentTutorialLoading && currentTutorial) {
            setCurrentVideo(currentTutorial);
        } else if (!tutorialsLoading && tutorials && tutorials.length > 0) {
            const matchedVideo = tutorials.find(video => video.id === parseInt(tutorialId));
            setCurrentVideo(matchedVideo || tutorials[0]);
        }
    }, [currentTutorialLoading, currentTutorial, tutorialsLoading, tutorials, tutorialId]);

    useEffect(() => {
        if (!tutorialsLoading && tutorials && tutorials.length > 0) {
            tutorials.forEach(video => {
                getVideoDuration(video.video).then(duration => {
                    setDurations(prevDurations => ({
                        ...prevDurations,
                        [video.slug]: duration,
                    }));
                });
            });
        }
        
        // Also calculate duration for current tutorial if it exists
        if (!currentTutorialLoading && currentTutorial) {
            getVideoDuration(currentTutorial.video).then(duration => {
                setDurations(prevDurations => ({
                    ...prevDurations,
                    [currentTutorial.slug]: duration,
                }));
            });
        }
    }, [tutorialsLoading, tutorials, currentTutorialLoading, currentTutorial]);

    // Fetch comments when current video changes
    useEffect(() => {
        if (currentVideo?.id) {
            fetchComments();
        }
    }, [currentVideo?.id]);

    const getVideoDuration = (videoUrl) => {
        return new Promise((resolve) => {
            const video = document.createElement('video');
            video.src = domain + videoUrl;
            video.preload = 'metadata';
            video.onloadedmetadata = () => {
                resolve(formatDuration(video.duration));
            };
            video.onerror = () => {
                resolve('N/A');
            };
        });
    };

    const formatDuration = (duration) => {
        const hours = Math.floor(duration / 3600);
        const minutes = Math.floor((duration % 3600) / 60);
        const seconds = Math.floor(duration % 60);

        let formattedDuration = '';
        if (hours > 0) {
            formattedDuration += `${hours}h `;
        }
        if (minutes > 0 || hours > 0) {
            formattedDuration += `${minutes}m `;
        }
        formattedDuration += `${seconds}s`;

        return formattedDuration.trim();
    };

    const handleVideoChange = (video) => {
        setCurrentVideo(video);
        setIsPlaying(false);
        setCurrentTime(0);
        navigate(`/how-it-works/${video.id}`);
    };

    function formatDate(date) {
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return date.toLocaleDateString('en-US', options);
    }

    const handleVideoPlay = () => {
        setIsPlaying(true);
    };

    const handleVideoPause = () => {
        setIsPlaying(false);
    };

    const handleTimeUpdate = () => {
        if (videoRef.current) {
            const current = videoRef.current.currentTime;
            const total = videoRef.current.duration;
            setCurrentTime(current);
            setDuration(total);
        }
    };

    const handleSeek = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const width = rect.width;
        const seekTime = (clickX / width) * duration;
        if (videoRef.current) {
            videoRef.current.currentTime = seekTime;
        }
    };

    const toggleMute = () => {
        if (videoRef.current) {
            videoRef.current.muted = !isMuted;
            setIsMuted(!isMuted);
        }
    };

    const toggleFullscreen = () => {
        if (videoRef.current) {
            if (!isFullscreen) {
                videoRef.current.requestFullscreen();
            } else {
                document.exitFullscreen();
            }
            setIsFullscreen(!isFullscreen);
        }
    };

    const shareVideo = () => {
        if (navigator.share) {
            navigator.share({
                title: currentVideo?.title,
                text: currentVideo?.description,
                url: window.location.href,
            });
        } else {
            navigator.clipboard.writeText(window.location.href);
            // You can add a toast notification here
        }
    };

    const fetchComments = async () => {
        if (!currentVideo?.id) return;
        
        setCommentsLoading(true);
        try {
            const headers = {
                'Content-Type': 'application/json'
            };
            
            // Only add authorization header if user is logged in
            if (isLoggedIn && userInfo?.accessToken) {
                headers['Authorization'] = `Bearer ${userInfo.accessToken}`;
            }
            
            const response = await fetch(`${domain}/tutorials/${currentVideo.id}/comments`, {
                headers
            });
            
            if (response.ok) {
                const data = await response.json();
                setComments(data);
            } else if (response.status === 401) {
                // User not authenticated, set empty comments
                setComments([]);
            }
        } catch (error) {
            console.error('Error fetching comments:', error);
            setComments([]);
        } finally {
            setCommentsLoading(false);
        }
    };

    const addComment = async () => {
        if (!comment.trim() || !currentVideo?.id) return;
        
        if (!isLoggedIn) {
            alert('Please log in to add a comment');
            return;
        }
        
        try {
            const response = await fetch(`${domain}/tutorials/${currentVideo.id}/comments`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${userInfo.accessToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    content: comment.trim()
                })
            });
            
            if (response.ok) {
                const newComment = await response.json();
            setComments([newComment, ...comments]);
            setComment('');
            } else if (response.status === 401) {
                alert('Please log in to add a comment');
            }
        } catch (error) {
            console.error('Error adding comment:', error);
        }
    };

    const likeComment = async (commentId) => {
        if (!isLoggedIn) {
            alert('Please log in to like comments');
            return;
        }
        
        try {
            const response = await fetch(`${domain}/comments/${commentId}/like`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${userInfo.accessToken}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (response.ok) {
                const updatedComment = await response.json();
        setComments(comments.map(c => 
                    c.id === commentId ? updatedComment : c
                ));
            } else if (response.status === 401) {
                alert('Please log in to like comments');
            }
        } catch (error) {
            console.error('Error liking comment:', error);
        }
    };

    const deleteComment = async (commentId) => {
        if (!isLoggedIn) {
            alert('Please log in to delete comments');
            return;
        }
        
        try {
            const response = await fetch(`${domain}/comments/${commentId}/delete`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${userInfo.accessToken}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (response.ok) {
                setComments(comments.filter(c => c.id !== commentId));
            } else if (response.status === 401) {
                alert('Please log in to delete comments');
            }
        } catch (error) {
            console.error('Error deleting comment:', error);
        }
    };

    const startReply = (comment) => {
        if (!isLoggedIn) {
            alert('Please log in to reply to comments');
            return;
        }
        setReplyingTo(comment);
        setReplyContent('');
    };

    const cancelReply = () => {
        setReplyingTo(null);
        setReplyContent('');
    };

    const submitReply = async () => {
        if (!replyContent.trim() || !replyingTo) return;
        
        try {
            const response = await fetch(`${domain}/comments/${replyingTo.id}/reply`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${userInfo.accessToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    content: replyContent.trim()
                })
            });
            
            if (response.ok) {
                const newReply = await response.json();
                // Update the parent comment with the new reply
                setComments(comments.map(comment => {
                    if (comment.id === replyingTo.id) {
                        return {
                            ...comment,
                            replies: [...(comment.replies || []), newReply]
                        };
                    }
                    return comment;
                }));
                setReplyingTo(null);
                setReplyContent('');
            } else if (response.status === 401) {
                alert('Please log in to reply to comments');
            }
        } catch (error) {
            console.error('Error adding reply:', error);
        }
    };

    const toggleReplies = (commentId) => {
        setHiddenReplies(prev => {
            const newSet = new Set(prev);
            if (newSet.has(commentId)) {
                newSet.delete(commentId);
            } else {
                newSet.add(commentId);
            }
            return newSet;
        });
    };

    const downloadVideo = () => {
        if (currentVideo) {
            const link = document.createElement('a');
            link.href = domain + currentVideo.video;
            link.download = currentVideo.title;
            link.click();
        }
    };

    const tabs = [
        { id: 'overview', label: 'Overview', icon: <BookOpen className="w-4 h-4" /> },
        { id: 'transcript', label: 'Transcript', icon: <MessageCircle className="w-4 h-4" /> },
        { id: 'resources', label: 'Resources', icon: <Download className="w-4 h-4" /> },
        { id: 'comments', label: 'Comments', icon: <Users className="w-4 h-4" /> }
    ];

    const relatedVideos = tutorials?.filter(video => 
        video.slug !== currentVideo?.slug && 
        (video.category === currentVideo?.category || video.difficulty === currentVideo?.difficulty)
    ).slice(0, 6) || [];

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
            <Header />
            
            <div className="container mx-auto px-4 py-8 max-w-7xl">
                {/* Breadcrumb */}
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5 }}
                    className="mb-6"
                >
                    <button 
                        onClick={() => navigate('/how-it-works')}
                        className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors duration-200 group"
                    >
                        <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                        <span className="text-sm font-medium">Back to How It Works</span>
                    </button>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                    {/* Main Video Section */}
                    <div className="lg:col-span-3">
                    {tutorialsLoading || currentTutorialLoading ? (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                                className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6"
                            >
                                <div className="animate-pulse">
                                    <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
                                    <div className="h-96 bg-gray-200 rounded-lg mb-4"></div>
                                    <div className="space-y-2">
                                        <div className="h-4 bg-gray-200 rounded w-full"></div>
                                        <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                                        <div className="h-4 bg-gray-200 rounded w-4/6"></div>
                                    </div>
                        </div>
                            </motion.div>
                    ) : currentVideo && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                                className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
                            >
                                {/* Video Header */}
                                <div className="p-6 border-b border-gray-100">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full font-medium">
                                                    {currentVideo.category || 'General'}
                                                </span>
                                                {currentVideo.difficulty && (
                                                    <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                                                        currentVideo.difficulty === 'beginner' ? 'bg-green-100 text-green-800' :
                                                        currentVideo.difficulty === 'intermediate' ? 'bg-yellow-100 text-yellow-800' :
                                                        'bg-red-100 text-red-800'
                                                    }`}>
                                                        {currentVideo.difficulty}
                                                    </span>
                                                )}
                                            </div>
                                            <h1 className="text-lg font-bold text-gray-900 mb-2">
                                                {currentVideo.title}
                                            </h1>
                                            <div className="flex items-center gap-4 text-sm text-gray-600">
                                                <div className="flex items-center gap-1">
                                                    <Calendar className="w-4 h-4" />
                                                    <span>{formatDate(new Date(currentVideo.created))}</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <Clock className="w-4 h-4" />
                                                    <span>{durations[currentVideo.slug] || "Loading..."}</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <Eye className="w-4 h-4" />
                                                    <span>{currentVideo.views || 0} views</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <Star className="w-4 h-4 text-yellow-500 fill-current" />
                                                    <span>{currentVideo.rating || stats?.average_rating || 4.8}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => setIsLiked(!isLiked)}
                                                className={`p-2 rounded-lg transition-all duration-200 ${
                                                    isLiked ? 'bg-red-50 text-red-600' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                                }`}
                                            >
                                                <Heart className={`w-4 h-4 ${isLiked ? 'fill-current' : ''}`} />
                                            </button>
                                            <button
                                                onClick={() => setIsBookmarked(!isBookmarked)}
                                                className={`p-2 rounded-lg transition-all duration-200 ${
                                                    isBookmarked ? 'bg-teal-50 text-teal-600' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                                }`}
                                            >
                                                <Bookmark className={`w-4 h-4 ${isBookmarked ? 'fill-current' : ''}`} />
                                            </button>
                                            <button
                                                onClick={shareVideo}
                                                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-all duration-200"
                                            >
                                                <Share2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Video Player */}
                                <div className="relative group" onMouseEnter={() => setShowControls(true)} onMouseLeave={() => setShowControls(false)}>
                            <video
                                        ref={videoRef}
                                controls
                                autoPlay
                                        onPlay={handleVideoPlay}
                                        onPause={handleVideoPause}
                                        onTimeUpdate={handleTimeUpdate}
                                        onLoadedMetadata={handleTimeUpdate}
                                src={domain + currentVideo.video}
                                        className="w-full h-auto max-h-[600px] object-cover"
                                        poster={domain + currentVideo.cover}
                                    />
                                    
                                    {/* Custom Video Controls Overlay */}
                                    <AnimatePresence>
                                        {showControls && (
                                            <motion.div
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                exit={{ opacity: 0 }}
                                                className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none"
                                            >
                                                <div className="absolute bottom-0 left-0 right-0 p-4 pointer-events-auto">
                                                    {/* Progress Bar */}
                                                    <div 
                                                        className="w-full h-1 bg-white/30 rounded-full cursor-pointer mb-2"
                                                        onClick={handleSeek}
                                                    >
                                                        <div 
                                                            className="h-full bg-white rounded-full transition-all duration-200"
                                                            style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
                                                        />
                                                    </div>
                                                    
                                                    {/* Controls */}
                                                    <div className="flex items-center justify-between text-white">
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                onClick={() => videoRef.current?.paused ? videoRef.current?.play() : videoRef.current?.pause()}
                                                                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                                                            >
                                                                {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                                                            </button>
                                                            <span className="text-sm">
                                                                {Math.floor(currentTime / 60)}:{(currentTime % 60).toFixed(0).padStart(2, '0')} / 
                                                                {Math.floor(duration / 60)}:{(duration % 60).toFixed(0).padStart(2, '0')}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                onClick={toggleMute}
                                                                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                                                            >
                                                                {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                                                            </button>
                                                            <button
                                                                onClick={toggleFullscreen}
                                                                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                                                            >
                                                                <Maximize2 className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>

                                {/* Video Content Tabs */}
                                <div className="p-6">
                                    {/* Tab Navigation */}
                                    <div className="flex items-center gap-1 mb-6 border-b border-gray-200">
                                        {tabs.map((tab) => (
                                            <button
                                                key={tab.id}
                                                onClick={() => setActiveTab(tab.id)}
                                                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-all duration-200 ${
                                                    activeTab === tab.id
                                                        ? 'text-orange-600 border-b-2 border-orange-600'
                                                        : 'text-gray-600 hover:text-gray-900'
                                                }`}
                                            >
                                                {tab.icon}
                                                {tab.label}
                                            </button>
                                        ))}
                                    </div>

                                    {/* Tab Content */}
                                    <AnimatePresence mode="wait">
                                        {activeTab === 'overview' && (
                                            <motion.div
                                                key="overview"
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -20 }}
                                                transition={{ duration: 0.3 }}
                                            >
                                                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                                    <BookOpen className="w-5 h-5 text-orange-600" />
                                                    Description
                                                </h3>
                                                <p className="text-gray-700 leading-relaxed mb-6">
                                                    {currentVideo.description}
                                                </p>
                                                
                                                {/* Learning Objectives */}
                                                {currentVideo.learning_objectives && currentVideo.learning_objectives.length > 0 && (
                                                    <div className="bg-orange-50 rounded-xl p-4 mb-6">
                                                        <h4 className="font-semibold text-orange-900 mb-3 flex items-center gap-2">
                                                        <Target className="w-4 h-4" />
                                                        What You'll Learn
                                                    </h4>
                                                        <ul className="space-y-2 text-orange-800">
                                                            {currentVideo.learning_objectives.map((objective, index) => (
                                                                <li key={index} className="flex items-start gap-2">
                                                                    <div className="w-1.5 h-1.5 bg-orange-600 rounded-full mt-2 flex-shrink-0"></div>
                                                                    <span>{objective}</span>
                                                        </li>
                                                            ))}
                                                    </ul>
                                                </div>
                                                )}
                                                
                                                {/* Video Stats */}
                                                <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-100">
                                                    <div className="text-center">
                                                        <div className="text-2xl font-bold text-orange-600">{currentVideo.rating || stats?.average_rating || 4.8}★</div>
                                                        <div className="text-sm text-gray-600">Rating</div>
                                                    </div>
                                                    <div className="text-center">
                                                        <div className="text-2xl font-bold text-teal-600">{currentVideo.views || 0}</div>
                                                        <div className="text-sm text-gray-600">Views</div>
                                                    </div>
                                                    <div className="text-center">
                                                        <div className="text-2xl font-bold text-purple-600">{stats?.total_tutorials || 0}</div>
                                                        <div className="text-sm text-gray-600">Total Videos</div>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        )}

                                        {activeTab === 'transcript' && (
                                            <motion.div
                                                key="transcript"
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -20 }}
                                                transition={{ duration: 0.3 }}
                                            >
                                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Video Transcript</h3>
                                                <div className="bg-gray-50 rounded-xl p-4 max-h-96 overflow-y-auto">
                                                    {currentVideo.transcript ? (
                                                        <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                                                            {currentVideo.transcript}
                                                        </p>
                                                    ) : (
                                                        <p className="text-gray-500 italic">
                                                            Transcript not available for this video.
                                                        </p>
                                                    )}
                                                </div>
                                            </motion.div>
                                        )}

                                        {activeTab === 'resources' && (
                                            <motion.div
                                                key="resources"
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -20 }}
                                                transition={{ duration: 0.3 }}
                                            >
                                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Resources</h3>
                                                <div className="space-y-3">
                                                    {/* Download Video - Always available */}
                                                    <button
                                                        onClick={downloadVideo}
                                                        className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors duration-200"
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <Download className="w-5 h-5 text-orange-600" />
                                                            <div className="text-left">
                                                                <div className="font-medium text-gray-900">Download Video</div>
                                                                <div className="text-sm text-gray-600">Save for offline viewing</div>
                                                            </div>
                                                        </div>
                                                        <ArrowRight className="w-4 h-4 text-gray-400" />
                                                    </button>
                                                    
                                                    {/* Dynamic resources from backend */}
                                                    {currentVideo.resources && currentVideo.resources.length > 0 ? (
                                                        currentVideo.resources.map((resource, index) => (
                                                    <a
                                                                key={index}
                                                                href={resource.url || "#"}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                        className="block w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors duration-200"
                                                    >
                                                        <div className="flex items-center gap-3">
                                                                    {resource.icon === 'document' && <BookOpen className="w-5 h-5 text-teal-600" />}
                                                                    {resource.icon === 'link' && <Globe className="w-5 h-5 text-blue-600" />}
                                                                    {resource.icon === 'download' && <Download className="w-5 h-5 text-green-600" />}
                                                                    {resource.icon === 'video' && <Video className="w-5 h-5 text-purple-600" />}
                                                                    {!resource.icon && <BookOpen className="w-5 h-5 text-gray-600" />}
                                                            <div className="text-left">
                                                                        <div className="font-medium text-gray-900">{resource.title}</div>
                                                                        <div className="text-sm text-gray-600">{resource.description}</div>
                                                            </div>
                                                        </div>
                                                        <ArrowRight className="w-4 h-4 text-gray-400" />
                                                    </a>
                                                        ))
                                                    ) : (
                                                        <div className="text-center py-8 text-gray-500">
                                                            <BookOpen className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                                                            <p>No additional resources available for this tutorial.</p>
                                                            </div>
                                                    )}
                            </div>
                        </motion.div>
                    )}

                                        {activeTab === 'comments' && (
                    <motion.div
                                                key="comments"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -20 }}
                                                transition={{ duration: 0.3 }}
                                            >
                                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Comments ({comments.length})</h3>
                                                
                                                {/* Add Comment */}
                                                <div className="mb-6">
                                                    {isLoggedIn ? (
                                                    <div className="flex gap-3">
                                                            <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                                                                {userInfo?.username?.charAt(0) || 'U'}
                                                        </div>
                                                        <div className="flex-1">
                                                            <textarea
                                                                value={comment}
                                                                onChange={(e) => setComment(e.target.value)}
                                                                placeholder="Add a comment..."
                                                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                                                                rows="3"
                                                            />
                                                            <div className="flex items-center justify-between mt-2">
                                                                <button
                                                                    onClick={addComment}
                                                                    disabled={!comment.trim()}
                                                                        className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                                                >
                                                                    Post Comment
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    ) : (
                                                        <div className="text-center py-6 bg-gray-50 rounded-lg">
                                                            <MessageCircle className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                                                            <p className="text-gray-600 mb-3">Please log in to add a comment</p>
                                                            <button
                                                                onClick={() => navigate('/login')}
                                                                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                                                            >
                                                                Log In
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Comments List */}
                                                {commentsLoading ? (
                                                <div className="space-y-4">
                                                        {[...Array(3)].map((_, index) => (
                                                            <div key={index} className="flex gap-3 animate-pulse">
                                                                <div className="w-8 h-8 bg-gray-300 rounded-full"></div>
                                                                <div className="flex-1">
                                                                    <div className="h-4 bg-gray-300 rounded w-1/4 mb-2"></div>
                                                                    <div className="h-3 bg-gray-300 rounded w-full mb-2"></div>
                                                                    <div className="h-3 bg-gray-300 rounded w-3/4"></div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="space-y-4">
                                                        {comments.length > 0 ? (
                                                            comments.map((comment) => (
                                                                <div key={comment.id} className="space-y-3">
                                                                    {/* Main Comment */}
                                                                    <div className="flex gap-3">
                                                                        <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                                                                            {comment.user_name?.charAt(0) || 'U'}
                                                            </div>
                                                            <div className="flex-1">
                                                                <div className="flex items-center gap-2 mb-1">
                                                                                <span className="font-medium text-gray-900">{comment.user_name}</span>
                                                                                <span className="text-sm text-gray-500">{comment.time_ago}</span>
                                                                </div>
                                                                            <p className="text-gray-700 mb-2">{comment.content}</p>
                                                                <div className="flex items-center gap-4">
                                                                    <button
                                                                        onClick={() => likeComment(comment.id)}
                                                                                    className="flex items-center gap-1 text-sm text-gray-500 hover:text-orange-600 transition-colors"
                                                                    >
                                                                        <ThumbsUp className="w-3 h-3" />
                                                                        {comment.likes}
                                                                    </button>
                                                                                                                                                                <button 
                                                                                    onClick={() => startReply(comment)}
                                                                                    className="text-sm text-gray-500 hover:text-gray-700 transition-colors flex items-center gap-1"
                                                                                >
                                                                        Reply
                                                                                    {comment.replies && comment.replies.length > 0 && (
                                                                                        <span className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded-full">
                                                                                            {comment.replies.length}
                                                                                        </span>
                                                                                    )}
                                                                    </button>
                                                                                {isLoggedIn && comment.user === userInfo?.id && (
                                                                                    <button 
                                                                                        onClick={() => deleteComment(comment.id)}
                                                                                        className="text-sm text-red-500 hover:text-red-700 transition-colors"
                                                                                    >
                                                                                        Delete
                                                                    </button>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    
                                                                    {/* Reply Form */}
                                                                    {replyingTo?.id === comment.id && (
                                                                        <div className="ml-8 mt-3 p-3 bg-gray-50 rounded-lg">
                                                                            <div className="flex gap-3">
                                                                                <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center text-white text-xs font-medium">
                                                                                    {userInfo?.username?.charAt(0) || 'U'}
                                                                                </div>
                                                                                <div className="flex-1">
                                                                                    <textarea
                                                                                        value={replyContent}
                                                                                        onChange={(e) => setReplyContent(e.target.value)}
                                                                                        placeholder={`Reply to ${comment.user_name}...`}
                                                                                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none text-sm"
                                                                                        rows="2"
                                                                                    />
                                                                                    <div className="flex items-center gap-2 mt-2">
                                                                                        <button
                                                                                            onClick={submitReply}
                                                                                            disabled={!replyContent.trim()}
                                                                                            className="px-3 py-1 bg-orange-600 text-white rounded text-sm hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                                                                        >
                                                                                            Reply
                                                                                        </button>
                                                                                        <button
                                                                                            onClick={cancelReply}
                                                                                            className="px-3 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400 transition-colors"
                                                                                        >
                                                                                            Cancel
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                    
                                                                    {/* Replies Toggle Button */}
                                                                    {comment.replies && comment.replies.length > 0 && (
                                                                        <div className="ml-8 mt-3">
                                                                            <button
                                                                                onClick={() => toggleReplies(comment.id)}
                                                                                className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                                                                            >
                                                                                {hiddenReplies.has(comment.id) ? (
                                                                                    <>
                                                                                        <ChevronDown className="w-4 h-4" />
                                                                                        Show {comment.replies.length} {comment.replies.length === 1 ? 'reply' : 'replies'}
                                                                                    </>
                                                                                ) : (
                                                                                    <>
                                                                                        <ChevronUp className="w-4 h-4" />
                                                                                        Hide {comment.replies.length} {comment.replies.length === 1 ? 'reply' : 'replies'}
                                                                                    </>
                                                                                )}
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                    
                                                                    {/* Replies */}
                                                                    {comment.replies && comment.replies.length > 0 && !hiddenReplies.has(comment.id) && (
                                                                        <div className="ml-8 space-y-3">
                                                                            {comment.replies.map((reply) => (
                                                                                <div key={reply.id} className="flex gap-3">
                                                                                    <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center text-white text-xs font-medium">
                                                                                        {reply.user_name?.charAt(0) || 'U'}
                                                                                    </div>
                                                                                    <div className="flex-1">
                                                                                        <div className="flex items-center gap-2 mb-1">
                                                                                            <span className="font-medium text-gray-900 text-sm">{reply.user_name}</span>
                                                                                            <span className="text-xs text-gray-500">{reply.time_ago}</span>
                                                                                        </div>
                                                                                        <p className="text-gray-700 text-sm mb-2">{reply.content}</p>
                                                                                        <div className="flex items-center gap-3">
                                                                                            <button
                                                                                                onClick={() => likeComment(reply.id)}
                                                                                                className="flex items-center gap-1 text-xs text-gray-500 hover:text-orange-600 transition-colors"
                                                                                            >
                                                                                                <ThumbsUp className="w-3 h-3" />
                                                                                                {reply.likes}
                                                                                            </button>
                                                                                            <button
                                                                                                onClick={() => startReply(comment)} // Reply to parent comment
                                                                                                className="text-xs text-gray-500 hover:text-gray-700 transition-colors flex items-center gap-1"
                                                                                            >
                                                                                                Reply
                                                                                                {comment.replies && comment.replies.length > 0 && (
                                                                                                    <span className="text-xs bg-gray-200 text-gray-600 px-1 py-0.5 rounded-full">
                                                                                                        {comment.replies.length}
                                                                                                    </span>
                                                                                                )}
                                                                                            </button>
                                                                                            {isLoggedIn && reply.user === userInfo?.id && (
                                                                                                <button 
                                                                                                    onClick={() => deleteComment(reply.id)}
                                                                                                    className="text-xs text-red-500 hover:text-red-700 transition-colors"
                                                                                                >
                                                                                                    Delete
                                                                                                </button>
                                                                                            )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                                                    )}
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <div className="text-center py-8 text-gray-500">
                                                                <MessageCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                                                                <p>No comments yet. Be the first to share your thoughts!</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            </motion.div>
                        )}
                    </div>

                    {/* Sidebar - Video List */}
                    <div className="lg:col-span-1">
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                            className="sticky top-8 space-y-6 z-0"
                        >
                            {/* Related Videos */}
                            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                    <TrendingUp className="w-5 h-5 text-orange-600" />
                                    Related Videos
                                </h3>
                                
                                <div className="space-y-4">
                                    {relatedVideos.map((video, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3, delay: index * 0.1 }}
                                            onClick={() => handleVideoChange(video)}
                                            className={`group cursor-pointer rounded-xl p-3 transition-all duration-200 hover:bg-gray-50 border-2 ${
                                                currentVideo?.slug === video.slug 
                                                    ? 'border-orange-500 bg-orange-50' 
                                                    : 'border-transparent hover:border-gray-200'
                                            }`}
                                        >
                                            <div className="relative mb-3">
                                    <img
                                        src={domain + video.cover}
                                        alt={video.title}
                                                    className="w-full h-24 object-cover rounded-lg group-hover:scale-105 transition-transform duration-200"
                                                />
                                                <div className="absolute inset-0 bg-black/20 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                                                    <Play className="w-6 h-6 text-white" />
                                                </div>
                                                <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                                            {durations[video.slug] || "Loading..."}
                                                </div>
                                            </div>
                                            
                                            <h4 className="font-medium text-gray-900 text-sm mb-1 line-clamp-2 group-hover:text-orange-600 transition-colors duration-200">
                                                {video.title}
                                            </h4>
                                            
                                            <div className="flex items-center gap-2 text-xs text-gray-500">
                                                <Calendar className="w-3 h-3" />
                                                <span>{formatDate(new Date(video.created))}</span>
                                            </div>
                                </motion.div>
                            ))}
                                </div>
                            </div>

                            {/* Quick Stats */}
                            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                    <Users className="w-4 h-4 text-teal-600" />
                                    Platform Stats
                                </h4>
                                <div className="space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Total Videos</span>
                                        <span className="font-medium text-gray-900">{statsLoading ? '...' : stats?.total_tutorials || 0}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Total Views</span>
                                        <span className="font-medium text-gray-900">{statsLoading ? '...' : stats?.total_tutorial_views || 0}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Avg. Rating</span>
                                        <span className="font-medium text-gray-900 flex items-center gap-1">
                                            {statsLoading ? '...' : stats?.average_rating || 4.8} <Star className="w-3 h-3 text-yellow-500 fill-current" />
                                        </span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Total Users</span>
                                        <span className="font-medium text-gray-900">{statsLoading ? '...' : stats?.total_users || 0}</span>
                                    </div>
                                </div>
                        </div>
                    </motion.div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HowItWorksDetails;